import React from 'react';
import styled from 'styled-components';
import { useMavrkPayTransactions } from '../../hooks/useMavrkPayTransactions';

interface RecentDepositsProps {
  userAddress: `0x${string}` | undefined;
}

const Container = styled.div`
  width: 100%;
  margin-top: 32px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 16px 0;
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TransactionRow = styled.div`
  padding: 12px 16px;
  background: rgba(168, 85, 247, 0.08);
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(168, 85, 247, 0.12);
    border-color: rgba(168, 85, 247, 0.3);
  }
`;

const DepositInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const Amount = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #10b981;
`;

const ViewOnExplorer = styled.a`
  font-size: 11px;
  color: #a855f7;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    color: #d8b4fe;
    text-decoration: underline;
  }
`;

const DateTime = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const DateText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`;

const TimeText = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const LoadingText = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  padding: 20px;
`;

const NoTransactions = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  padding: 20px;
  font-style: italic;
`;

const RecentDeposits: React.FC<RecentDepositsProps> = ({ userAddress }) => {
  // Fetch deposits from Supabase (much faster than blockchain queries)
  const { transactions: deposits, loading } = useMavrkPayTransactions(userAddress, 'deposit', 5);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${hours}:${minutes} UTC`;
  };

  if (!userAddress) {
    return null;
  }

  return (
    <Container>
      <Title>Recent Deposits</Title>
      
      {loading && <LoadingText>Loading deposits...</LoadingText>}
      
      {!loading && deposits.length === 0 && (
        <NoTransactions>No recent deposits</NoTransactions>
      )}
      
      {!loading && deposits.length > 0 && (
        <TransactionList>
          {deposits.map((tx) => (
            <TransactionRow key={tx.id}>
              <DepositInfo>
                <Amount>+{tx.amount} USDG</Amount>
                {tx.tx_hash && (
                  <ViewOnExplorer
                    href={`https://explorer.inkonchain.com/tx/${tx.tx_hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Explorer →
                  </ViewOnExplorer>
                )}
              </DepositInfo>
              <DateTime>
                <DateText>{formatDate(tx.created_at)}</DateText>
                <TimeText>{formatTime(tx.created_at)}</TimeText>
              </DateTime>
            </TransactionRow>
          ))}
        </TransactionList>
      )}
    </Container>
  );
};

export default RecentDeposits;
